import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash, X } from "@phosphor-icons/react";

// Signature-box colours picked to be distinguishable on any form background.
const COLORS = [
  "#0F62FE", "#DA1E28", "#24A148", "#8A3FFC", "#F1C21B",
  "#FA4D56", "#08BDBA", "#EE538B", "#4589FF", "#33B1FF",
];

// Parse the <div class="pdf-form-page"> blocks out of the resolution body HTML.
// Each page keeps its native PDF-point dimensions, the base64 PNG src, and
// (when available) the word-level text positions the backend extracted at
// upload time — used by the Auto-detect button to guess signature spots.
function parsePages(html) {
  if (typeof window === "undefined" || !html) return [];
  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.querySelectorAll(".pdf-form-page"));
  return nodes.map((el) => {
    const style = el.getAttribute("style") || "";
    const wm = style.match(/width:\s*([\d.]+)pt/);
    const hm = style.match(/height:\s*([\d.]+)pt/);
    const img = el.querySelector("img");
    let words = [];
    const raw = el.getAttribute("data-words");
    if (raw) {
      try { words = JSON.parse(raw); } catch { words = []; }
    }
    return {
      width: wm ? parseFloat(wm[1]) : 595,
      height: hm ? parseFloat(hm[1]) : 842,
      src: (img && img.getAttribute("src")) || "",
      words,
    };
  });
}

// Try to find where each signatory's signature box should go by scanning the
// PDF words extracted at upload time. Strategy: look for the signatory's name
// as consecutive words on a page — signatures typically sit ABOVE the printed
// name. If a page has multiple occurrences of the same name, later signatories
// with that name pick later occurrences.
function autoDetectPlacements(pages, signatories) {
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  // Build (name, occurrenceIndex) queue — same-named entries get sequential picks.
  const usedByName = new Map();
  const findOccurrences = (name) => {
    // Return list of { page, x, y, w, h } matches across all pages, in order.
    const parts = norm(name).split(" ").filter(Boolean);
    if (parts.length === 0) return [];
    const hits = [];
    pages.forEach((p, pi) => {
      const ws = p.words || [];
      // Slide a window of size `parts.length` across page words and match.
      for (let i = 0; i <= ws.length - parts.length; i++) {
        let ok = true;
        for (let j = 0; j < parts.length; j++) {
          if (norm(ws[i + j].t) !== parts[j]) { ok = false; break; }
        }
        if (!ok) continue;
        // Compute bounding box across the matched words.
        const first = ws[i], last = ws[i + parts.length - 1];
        const x = first.x;
        const y = first.y;
        const w = (last.x + last.w) - first.x;
        const h = Math.max(first.h, last.h);
        hits.push({ page: pi, x, y, w, h });
      }
    });
    return hits;
  };
  return signatories.map((s) => {
    if (s.placement) return s; // don't overwrite existing placements
    const key = norm(s.name);
    const hits = findOccurrences(s.name);
    if (hits.length === 0) return s;
    const used = usedByName.get(key) || 0;
    const hit = hits[Math.min(used, hits.length - 1)];
    usedByName.set(key, used + 1);
    // Signature sits directly above the printed name. Default box 160x44pt,
    // clamped to page bounds so the box stays fully visible.
    const boxW = 160, boxH = 44;
    const pg = pages[hit.page];
    const x = Math.max(0, Math.min(pg.width - boxW, hit.x));
    const y = Math.max(0, Math.min(pg.height - boxH, hit.y - boxH - 4));
    return { ...s, placement: { page: hit.page, x, y, w: boxW, h: boxH } };
  });
}

const DEFAULT_BOX = { w: 160, h: 44 };

/**
 * PlacementEditor — drag a signature box onto each PDF page for each signatory.
 * Coordinates stored are in PDF points (top-left origin) so the backend can
 * overlay signatures at the exact spot when the PDF is rebuilt.
 */
function FreeFormAdd({ onAdd }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("Director");
  const submit = () => {
    const n = name.trim();
    if (!n) return;
    onAdd(n, role);
    setName("");
  };
  return (
    <div className="space-y-1">
      <input type="text" value={name}
             onChange={(e) => setName(e.target.value)}
             onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
             placeholder="Full name"
             className="w-full px-2 py-1 text-xs border border-border rounded-sm"
             data-testid="freeform-signatory-name" />
      <div className="flex gap-1">
        <select value={role} onChange={(e) => setRole(e.target.value)}
                className="flex-1 px-2 py-1 text-[11px] border border-border rounded-sm bg-white"
                data-testid="freeform-signatory-role">
          <option>Director</option>
          <option>Secretary</option>
          <option>Shareholder</option>
          <option>Witness</option>
          <option>Signatory</option>
        </select>
        <button type="button" onClick={submit}
                className="px-2 py-1 text-[11px] font-mono uppercase tracking-widest bg-[#0F62FE] text-white rounded-sm hover:bg-[#0043CE] disabled:opacity-40"
                disabled={!name.trim()}
                data-testid="freeform-signatory-add">Add</button>
      </div>
    </div>
  );
}

export default function PlacementEditor({ body, signatories, onChange, availableSignatories = [] }) {
  const pages = useMemo(() => parsePages(body), [body]);
  // Auto-select the first unplaced signatory so the drafter can click on the
  // letter immediately without having to figure out the workflow. Falls back
  // to 0 if everyone is placed already.
  const firstUnplaced = signatories.findIndex(s => !s.placement);
  const [activeIdx, setActiveIdx] = useState(firstUnplaced >= 0 ? firstUnplaced : 0);
  const dragRef = useRef(null); // { i, offX, offY }

  // Has the backend given us word positions for at least one page? If not,
  // the PDF was uploaded before word extraction was added — auto-detect is
  // effectively useless.
  const hasWordData = pages.some((p) => (p.words || []).length > 0);
  const runAutoDetect = () => {
    const next = autoDetectPlacements(pages, signatories);
    const changed = next.filter((s, i) => (s.placement && !signatories[i].placement)).length;
    onChange(next);
    if (changed === 0) {
      window.alert("Could not detect any signature spot from the letter text. Please place them manually — click a name in the left panel then click on the letter.");
    } else {
      window.alert(`Auto-placed ${changed} signature box${changed > 1 ? "es" : ""}. Drag any box to fine-tune, then click Save.`);
    }
  };

  if (pages.length === 0) {
    return (
      <div className="border border-dashed border-border p-8 text-sm text-muted-foreground">
        No PDF pages detected. Re-upload the source PDF to enable signature placement.
      </div>
    );
  }

  // Scale each page image so the widest page fits inside the dialog column.
  const maxDisplayWidth = 720;
  const scale = Math.min(1, maxDisplayWidth / Math.max(...pages.map((p) => p.width)));

  const setPlacement = (i, patch) => {
    const next = signatories.map((s, idx) => (idx === i ? { ...s, placement: patch } : s));
    onChange(next);
  };

  const removeSignatory = (i) => {
    const next = signatories.filter((_, idx) => idx !== i);
    onChange(next);
    if (activeIdx >= next.length) setActiveIdx(Math.max(0, next.length - 1));
  };

  const placeOnPage = (pageIdx, evt) => {
    if (dragRef.current) return; // drag handles its own end
    const target = evt.currentTarget;
    const rect = target.getBoundingClientRect();
    const px = (evt.clientX - rect.left) / scale;
    const py = (evt.clientY - rect.top) / scale;
    const page = pages[pageIdx];
    const w = DEFAULT_BOX.w;
    const h = DEFAULT_BOX.h;
    // Centre the box on click, clamp inside the page bounds.
    const x = Math.max(0, Math.min(page.width - w, px - w / 2));
    const y = Math.max(0, Math.min(page.height - h, py - h / 2));
    setPlacement(activeIdx, { page: pageIdx, x, y, w, h });
  };

  const onDragStart = (i, evt) => {
    evt.stopPropagation();
    const box = evt.currentTarget.getBoundingClientRect();
    dragRef.current = {
      i,
      offX: (evt.clientX - box.left) / scale,
      offY: (evt.clientY - box.top) / scale,
    };
    setActiveIdx(i);
    window.addEventListener("mousemove", onDragMove);
    window.addEventListener("mouseup", onDragEnd);
  };

  const onDragMove = (evt) => {
    const d = dragRef.current;
    if (!d) return;
    const sig = signatories[d.i];
    const pl = sig?.placement;
    if (!pl) return;
    const pageEl = document.querySelector(`[data-placement-page="${pl.page}"]`);
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    const px = (evt.clientX - rect.left) / scale - d.offX;
    const py = (evt.clientY - rect.top) / scale - d.offY;
    const page = pages[pl.page];
    const x = Math.max(0, Math.min(page.width - pl.w, px));
    const y = Math.max(0, Math.min(page.height - pl.h, py));
    setPlacement(d.i, { ...pl, x, y });
  };

  const onDragEnd = () => {
    dragRef.current = null;
    window.removeEventListener("mousemove", onDragMove);
    window.removeEventListener("mouseup", onDragEnd);
  };

  return (
    <div className="grid grid-cols-[280px_1fr] gap-4">
      {/* Signatory chip list */}
      <aside className="border border-border p-3 space-y-2 max-h-[70vh] overflow-y-auto sticky top-0 self-start">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
          Signatories · click to select, then click on the page
        </div>
        <Button size="sm" variant="outline" className="w-full rounded-sm h-8 mb-2"
                onClick={runAutoDetect}
                disabled={!hasWordData || signatories.length === 0}
                title={hasWordData
                  ? "Scan the letter for each signatory's printed name and drop a signature box just above it"
                  : "Re-upload this PDF (the current file has no text data — auto-detect only works for PDFs uploaded after this feature)"}
                data-testid="auto-detect-placements-btn">
          ✨ Auto-detect placements
        </Button>
        {signatories.length === 0 && (
          <div className="text-[11px] text-muted-foreground border border-dashed border-border p-2 mb-2"
               data-testid="no-signatories-hint">
            No signatories yet. Add someone from your client profile below, or type a name to add manually.
          </div>
        )}
        {signatories.map((s, i) => {
          const c = COLORS[i % COLORS.length];
          const placed = !!s.placement;
          return (
            <div key={i}
                 className={`px-3 py-2 border cursor-pointer flex items-center gap-2 ${
                   activeIdx === i ? "border-[#0F62FE] bg-[#0F62FE]/5" : "border-border"
                 }`}
                 onClick={() => setActiveIdx(i)}
                 data-testid={`placement-signatory-${i}`}>
              <span className="w-3 h-3 rounded-sm inline-block" style={{ background: c }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{s.name || `Signatory ${i + 1}`}</div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">
                  {placed ? `Page ${s.placement.page + 1} · placed` : "not placed"}
                </div>
              </div>
              {placed && (
                <Button size="icon" variant="ghost" className="h-6 w-6 text-[#DA1E28]"
                        onClick={(e) => { e.stopPropagation(); setPlacement(i, null); }}
                        title="Clear placement"
                        data-testid={`clear-placement-${i}`}>
                  <Trash size={12} weight="bold" />
                </Button>
              )}
              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-[#DA1E28]"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Remove ${s.name || "this signatory"} from the signing list?`)) removeSignatory(i);
                      }}
                      title="Remove signatory"
                      data-testid={`remove-signatory-${i}`}>
                <X size={12} weight="bold" />
              </Button>
            </div>
          );
        })}
        <p className="text-[11px] text-muted-foreground pt-2">
          Tip: <b>×</b> removes a signatory from the list · <b>trash</b> clears their placement · drag any box to nudge.
        </p>
        {availableSignatories.length > 0 && (
          <div className="pt-3 border-t border-border">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              + Add from client profile
            </div>
            {availableSignatories.map((s, i) => (
              <button key={i}
                      type="button"
                      onClick={() => {
                        const next = [...signatories, {
                          name: s.name, role: s.role || "Director", email: s.email || "", ic_no: s.ic_no || "",
                          placement: null,
                        }];
                        onChange(next);
                        // auto-select the new one so the drafter can click to place it
                        setActiveIdx(next.length - 1);
                      }}
                      className="w-full text-left px-2 py-1.5 text-xs border border-dashed border-border rounded-sm mb-1 hover:border-[#0F62FE] hover:bg-[#0F62FE]/5"
                      data-testid={`add-signatory-${i}`}>
                <span className="text-foreground">{s.name}</span>
                <span className="text-muted-foreground ml-1">· {s.role || "Signatory"}</span>
              </button>
            ))}
          </div>
        )}
        <div className="pt-3 border-t border-border">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            + Add manually
          </div>
          <FreeFormAdd onAdd={(name, role) => {
            const next = [...signatories, { name, role: role || "Signatory", email: "", ic_no: "", placement: null }];
            onChange(next);
            setActiveIdx(next.length - 1);
          }} />
        </div>
      </aside>

      {/* Pages */}
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2 bg-[#F3F4F6] p-3">
        {pages.map((p, pi) => (
          <div key={pi} className="relative mx-auto shadow-sm bg-white cursor-crosshair"
               data-placement-page={pi}
               style={{ width: p.width * scale, height: p.height * scale }}
               onClick={(e) => placeOnPage(pi, e)}>
            <img src={p.src} alt={`Page ${pi + 1}`} draggable="false"
                 style={{ width: "100%", height: "100%", display: "block",
                          pointerEvents: "none", userSelect: "none" }} />
            {signatories.map((s, i) => {
              const pl = s.placement;
              if (!pl || pl.page !== pi) return null;
              const c = COLORS[i % COLORS.length];
              return (
                <div key={i}
                     onMouseDown={(e) => onDragStart(i, e)}
                     onClick={(e) => { e.stopPropagation(); setActiveIdx(i); }}
                     style={{
                       position: "absolute",
                       left: pl.x * scale,
                       top: pl.y * scale,
                       width: pl.w * scale,
                       height: pl.h * scale,
                       border: `2px solid ${c}`,
                       background: `${c}22`,
                       cursor: "move",
                       fontSize: 10,
                       fontFamily: "monospace",
                       color: c,
                       display: "flex",
                       alignItems: "flex-end",
                       justifyContent: "flex-start",
                       padding: 2,
                       boxSizing: "border-box",
                     }}
                     data-testid={`placement-box-${i}`}>
                  <span style={{ background: "white", padding: "0 4px", lineHeight: "12px" }}>
                    {s.name || `#${i + 1}`}
                  </span>
                </div>
              );
            })}
            <div className="absolute top-1 right-1 font-mono text-[10px] uppercase text-muted-foreground bg-white/70 px-1">
              Page {pi + 1}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
